/**
 * Générateur — Automatisme : identifier des régularités et poursuivre une
 * suite de motifs évolutive.
 * Attendu ID : 1060 | Niveau : 5ème | Domaine : Calcul littéral et algébrique
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 600).
 * Cadrage du sous-agent programme-maths, arbitré par le prof à l'épreuve de
 * Musk les 2026-09-09 et 2026-09-10.
 *
 * Trois attendus, trois gestes, et c'est la NATURE DE LA RÉPONSE qui trie :
 *   1060  l'étape SUIVANTE, par itération, dans le registre figural ;
 *   1061  un NOMBRE, à un rang donné (surtout lointain) ;
 *   1062  un CALCUL non réduit qui décrit la fabrication (« 1 + 10 × 4 »).
 * Ici : repérer ce qui se répète d'une étape à la suivante et le reproduire.
 * « Combien ajoute-t-on à chaque étape ? » est encore 1060 ; « à l'étape 15 il
 * y en a 61 » est 1061 ; « 1 + 15 × 4 » est 1062.
 *
 * Le discriminant contre gen_638 (6e) : là-bas la règle est DONNÉE en toutes
 * lettres et la frise porte des étiquettes « + 5 » ; ici elle est À LIRE sur
 * la figure. C'est le mot « identifier ».
 *
 * Plan de cartes validé, UNE carte, « L'étape suivante » :
 *   - QCM d'IMAGES  (trois figures : laquelle est l'étape 4 ?)
 *   - Vrai/Faux     (3 phrases sur ce qui s'ajoute et sur les étapes proches)
 * Supprimés à l'épreuve de Musk : LA SÉRIE FLASH (« Étape 4 : ⬚ » demande un
 * nombre à un rang donné, donc c'est 1061, même avec de petits nombres :
 * proche contre lointain sépare deux difficultés, pas deux gestes ; constat
 * prof 2026-09-10), le calcul « 5 + 9 × 4 » (1061), la structure « 1 + N × 4 »
 * (1062), la lettre (932), les suites de
 * nombres pures et la règle fournie dans l'énoncé (638), l'étape 0 et
 * l'interpolation (Eduscol patterns p. 11 et 12 : on numérote à partir de 1,
 * et peu d'étapes données ouvrent plusieurs interprétations), la saisie nue.
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Banque commune aux trois attendus (./motifs.js), TOUTES les figures ici :
 * les structurées (croix, L, cadre, tours, allumettes en carrés et en
 * triangles), les rangées, et surtout les figures à AJOUTS CROISSANTS (carré
 * n², escalier), une série sur quatre : le programme CM2 montre 1, 4, 9, 16,
 * la régularité n'est pas toujours « + k », et c'est le seul endroit du parc
 * qui le teste. Le QCM d'images ne prend que les figures dont la FORME se
 * reconnaît (les rangées en sont exclues : à l'étape 4, un alignement de vingt
 * points se compte au lieu de se reconnaître).
 *
 * SACRIFIÉ pour le coup de pouce : le couple (fixe 1, groupe 4), la suite
 * 5 ; 9 ; 13 du carrelage de 638 ; il exclut la croix et les rangées (1, 4)
 * de 1060 et de 1061 (la croix reste en 1062, dont le sacrifice est (1, 3)).
 *
 * ── Distracteurs ─────────────────────────────────────────────────────────
 *   pas_poursuivi   : la figure de l'étape précédente, il n'a rien ajouté ;
 *   saut_de_trop    : la figure de l'étape 5, il a poursuivi deux fois ;
 *   mauvaise_forme  : le bon geste sur un AUTRE motif de même élément, il a
 *                     compté sans regarder la forme ;
 *   pas_mal_lu      : k ± 1 (Vrai/Faux) — sur les allumettes, c'est le côté
 *                     partagé compté deux fois (4 par carré au lieu de 3) ;
 *   pas_constant    : le premier écart répété sur une figure croissante ;
 *   ajout_de_trop   : la valeur de l'étape suivante donnée pour celle-ci.
 */
import { randomChoice, randInt, shuffle, makeQcm, makeVraiFaux } from '../../../utils';
import {
    FIGURES, STRUCTUREES, CROISSANTES, motif, tirerLineaire, tirerCroissant,
    elImage, imageEtape, paramsMotif, sansRepetition, pluriel,
} from './motifs';

const ATTENDU_ID = 1060;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_SUIVANTE = "L'étape suivante";

const SACRIFIE = { fixe: 1, groupe: 4 };

const EXPLICATIONS = (m) => ({
    regle: m.croissant
        ? 'Ce qui s\'ajoute grandit d\'une étape à l\'autre : regarde les trois étapes, pas seulement les deux premières.'
        : `D'une étape à la suivante, on ajoute toujours ${pluriel(m.groupe, m.nom)}.`,
    pas_poursuivi: 'C\'est la figure de l\'étape précédente : il faut ajouter ce qui s\'ajoute à chaque fois.',
    saut_de_trop: 'Celle-ci a un groupe de trop : c\'est l\'étape d\'après.',
    mauvaise_forme: 'Le nombre ne suffit pas : la figure doit garder la même forme.',
    pas_mal_lu: m.croissant ? 'Ce qui s\'ajoute grandit à chaque étape.' : `Compte ce qui s'ajoute d'une étape à l'autre : ${pluriel(m.groupe, m.nom)}.`,
    pas_constant: 'Ce qui s\'ajoute grandit à chaque étape : regarde les trois étapes.',
    ajout_de_trop: 'C\'est le nombre de l\'étape d\'après.',
});

// ─── Carte 2, format A : QCM d'images (laquelle est l'étape 4 ?) ──────────

/** Un autre motif, de MÊME élément (points, carreaux, allumettes) : la forme change, pas le matériau. */
const autreMotif = (m) => {
    const candidats = [...STRUCTUREES, ...CROISSANTES]
        .filter((f) => f !== m.figure && FIGURES[f].formes.includes(m.forme));
    if (!candidats.length) return null;
    const figure = randomChoice(candidats);
    return motif(figure, m.forme);
};

/**
 * Un choix de QCM en image : `imageData`, JAMAIS `imageUrl` (check L-QCM-IMAGEURL
 * du harnais, convention de gen_947 et gen_1002). C'est l'inverse des images
 * d'ÉNONCÉ, qui passent par `imageUrl` (l'upload banque rejette un imageData
 * non-base64) : les deux règles cohabitent, chacune sur son champ.
 */
const choixImage = (m, n) => ({ type: 'image', config: { imageData: imageEtape(m, n) } });

/**
 * Le QCM d'images ne prend que des figures dont la FORME se reconnaît d'un
 * coup d'œil : les rangées en sont exclues (à l'étape 4, un alignement de
 * vingt-cinq points se compte au lieu de se reconnaître, constat au rendu
 * 2026-09-10). Elles restent dans la Série flash, où la réponse est un nombre.
 */
const tirerReconnaissable = () => (randInt(1, 4) === 1
    ? tirerCroissant()
    : tirerLineaire({ sacrifie: SACRIFIE, structureesSeules: true }));

const genQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const m = tirerReconnaissable();
        const autre = autreMotif(m);
        if (!autre) continue;
        const bon = choixImage(m, 4);
        const faux = [
            { code: 'saut_de_trop', choix: choixImage(m, 5) },
            { code: randomChoice([true, false]) ? 'pas_poursuivi' : 'mauvaise_forme', choix: null },
        ];
        faux[1].choix = faux[1].code === 'pas_poursuivi' ? choixImage(m, 3) : choixImage(autre, 4);
        const urls = [bon, ...faux.map((f) => f.choix)].map((c) => c.config.imageData);
        if (new Set(urls).size < 3) continue;
        const texte = `{{element:0}}<p><b>Quelle figure est l'étape 4 ?</b></p>`;
        const payload = makeQcm(shuffle([bon, ...faux.map((f) => f.choix)]), bon, texte, { ...opts(L_SUIVANTE), elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { N: 4, autre: autre.figure, distracteurs: faux.map(({ code }) => ({ code })) });
        return payload;
    }
    // Repli déterministe : L de points, étape 4 contre étape 5 et étape 3.
    const m = motif('l', 'points');
    const bon = choixImage(m, 4);
    const payload = makeQcm([bon, choixImage(m, 5), choixImage(m, 3)], bon, `{{element:0}}<p><b>Quelle figure est l'étape 4 ?</b></p>`, { ...opts(L_SUIVANTE), elements_additionnels: elImage(m) });
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { N: 4, distracteurs: [{ code: 'saut_de_trop' }, { code: 'pas_poursuivi' }] });
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux ─────────────────────────────────────

/** « À chaque étape, on ajoute 3 carreaux. » */
const ligneAjout = (m, vraie) => {
    if (m.croissant) {
        const d1 = m.valeur(2) - m.valeur(1);
        return { ligne: `À chaque étape, on ajoute ${pluriel(d1, m.nom)}.`, vraie: false, code: 'pas_constant' };
    }
    if (vraie) return { ligne: `À chaque étape, on ajoute ${pluriel(m.groupe, m.nom)}.`, vraie: true, code: null };
    const x = randomChoice([m.groupe + 1, m.groupe - 1].filter((v) => v > 1));
    return { ligne: `À chaque étape, on ajoute ${pluriel(x, m.nom)}.`, vraie: false, code: 'pas_mal_lu' };
};

/**
 * « Ce qui s'ajoute est le même à chaque étape. » : le discriminant entre un
 * ajout constant et un ajout croissant, sans jamais donner d'effectif. Les
 * lignes « l'étape N a v » ont été retirées le 2026-09-10, pour la raison qui
 * a emporté la Série flash : un nombre à un rang donné, c'est 1061.
 */
const ligneConstance = () => {
    // la formulation se tire à pile ou face, la vérité en découle : sinon
    // « grandit » aurait toujours été la ligne fausse, repérable sans regarder
    const grandit = randomChoice([true, false]);
    return {
        ligne: grandit ? 'Ce qui s\'ajoute grandit à chaque étape.' : 'Ce qui s\'ajoute est le même à chaque étape.',
        vraie: !grandit,
        code: grandit ? 'pas_constant' : null,
    };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        // ajout CONSTANT seulement : sur une figure croissante, aucune phrase
        // « on ajoute X » ne peut être vraie, le tableau n'aurait plus de vraie.
        // Le discriminant croissant vit au QCM d'images.
        const m = tirerLineaire({ sacrifie: SACRIFIE });
        // deux lignes « on ajoute X » (une vraie, une fausse) et la ligne de constance
        const verites = shuffle([true, false]);
        const affs = shuffle([
            ligneAjout(m, verites[0]),
            ligneAjout(m, verites[1]),
            ligneConstance(),
        ]);
        const nbVraies = affs.filter((a) => a.vraie).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const texte = `<p><b>Vrai ou faux ?</b></p>{{element:0}}`;
        const payload = makeVraiFaux(lignes, affs.map((a) => (a.vraie ? 0 : 1)), texte, { ...opts(L_SUIVANTE), elements_additionnels: elImage(m) });
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
        payload._meta.params = paramsMotif(m, { verites: affs.map((a) => a.vraie), distracteurs: affs.filter((a) => a.code).map(({ code }) => ({ code })) });
        return payload;
    }
    // Repli déterministe : L de points (3 ; 5 ; 7) : « on ajoute 2 » vrai ; « on ajoute 3 » faux ; la constance vraie.
    const m = motif('l', 'points');
    const payload = makeVraiFaux(
        ['À chaque étape, on ajoute 2 points.', 'À chaque étape, on ajoute 3 points.', 'Ce qui s\'ajoute est le même à chaque étape.'],
        [0, 1, 0], `<p><b>Vrai ou faux ?</b></p>{{element:0}}`, { ...opts(L_SUIVANTE), elements_additionnels: elImage(m) },
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS(m) };
    payload._meta.params = paramsMotif(m, { verites: [true, false, true], distracteurs: [{ code: 'pas_mal_lu' }] });
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'banque commune (./motifs.js), TOUTES les figures : structurées, rangées, et les ajouts croissants (carré, escalier) une série sur quatre ; rien d\'écrit sur la figure, la règle est à lire (contre 638 où elle est donnée)',
        'étapes PROCHES seulement, 4 à 7 : un rang donné plus loin est 1061, le calcul structuré est 1062',
        'le couple (fixe 1, groupe 4), suite 5 ; 9 ; 13, SACRIFIÉ pour la fiche : il exclut la croix et les rangées (1, 4)',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    suivante_qcm_unique: 'QCM d\'IMAGES, trois figures : l\'étape 4 (clé), l\'étape 5 (saut_de_trop) et, une fois sur deux, l\'étape 3 (pas_poursuivi) ou l\'étape 4 d\'un AUTRE motif de même élément (mauvaise_forme, « le bon nombre ne suffit pas ») ; c\'est le format le plus proche de « produire la figure » qui soit corrigeable automatiquement, le dessin libre n\'existant pas',
    suivante_tableau_vf: 'trois phrases, 1 ou 2 vraies : « à chaque étape, on ajoute k » (fausse pas_mal_lu k ± 1, ou pas_constant sur une figure croissante) et DEUX lignes « l\'étape N a v » sur des étapes proches (4, 5, 6), l\'une vraie et l\'autre fausse (ajout_de_trop, pas_poursuivi, pas_constant)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    suivante_qcm_unique: genQcm,
    suivante_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'suivante', label: L_SUIVANTE, formatsList: [
        { key: 'suivante_qcm_unique', label: 'QCM' },
        { key: 'suivante_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // mémoire courte : jamais deux fois la même variante dans une série (cf. sansRepetition)
    const payload = sansRepetition(key, GENERATORS[key]);
    payload._meta.generatorKey = key;
    return payload;
};
